import { DomainDto } from '../../../../dtos/domain'
import { NotificationAutoSuggestFields } from '../../../../enums/notification-auto-suggest-fields.enum'
import { INotificationFieldSuggestions } from '../../../../interfaces/notification-field-suggestions'
import { extract } from '../../../../_utils/object'
import { NotificationFieldSettingsSource } from '@pushly/models/lib/interfaces'
import { IEnhancedNotificationFieldSuggestions, SuggestedFieldsForm } from './types'

/**
 * Identifies which auto-population source's field settings a gating check applies to.
 * - `content_suggestions`: Page Analysis (landing-URL) suggestions.
 * - `ai_recommendations`: Pushly AI (Sealab) generated variants.
 *
 * Derived from the `@pushly/models` struct keys so it stays in sync as sources are added.
 */
export type FieldSettingsSource = NotificationFieldSettingsSource

/**
 * Determines if a field can be shown based on per-source domain field settings.
 *
 * Resolution order:
 * - column-null (`notificationFieldSettings == null`): all fields allowed for every source.
 * - source-null (`notificationFieldSettings[source] == null`): all fields allowed for that source.
 * - non-null array: membership test against the field's mapped key (`imageUrl` → `image_url`;
 *   `title`/`body`/`keywords` map to their identical keys). An empty array disallows all fields.
 *
 * Each source is evaluated independently, so one source's value never affects the other's result.
 */
export function canShowField(
    domain: DomainDto,
    field: keyof INotificationFieldSuggestions,
    source: FieldSettingsSource,
): boolean {
    const settings = domain.notificationFieldSettings
    // column-null → all fields enabled for both sources (backward-compatible default)
    if (settings == null) return true

    const allowedFields = settings[source]
    // source-null → all fields enabled for this source
    if (allowedFields == null) return true

    // non-null array → membership test ([] disallows every field for this source)
    const key = (field === 'imageUrl' ? 'image_url' : field) as NotificationAutoSuggestFields
    return allowedFields.includes(key)
}

/**
 * Sorts keywords alphabetically for display
 */
export function sortKeywordsForDisplay(keywords: string[] | null): string[] {
    if (!keywords || !keywords.length) return []

    // First, sort alphabetically
    const sorted = [...keywords].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))

    // For flexbox row-major layout to appear column-major, we need to reorder items
    // If we have items [A,B,C,D,E,F] and want 3 columns:
    // Column layout: A D    Row layout needed: A B C
    //                B E                       D E F
    //                C F
    // So we need to arrange as: [A,B,C,D,E,F] -> [A,D,B,E,C,F]

    const columns = 3
    const rows = Math.ceil(sorted.length / columns)
    const reordered: string[] = []

    // Fill by rows, but pick items from column positions
    for (let row = 0; row < rows; row++) {
        for (let col = 0; col < columns; col++) {
            const index = col * rows + row
            if (index < sorted.length) {
                reordered.push(sorted[index])
            }
        }
    }

    return reordered
}

/**
 * Extracts form selections for page analyzer results
 */
export function extractFormSelections(
    values: SuggestedFieldsForm,
    variant: IEnhancedNotificationFieldSuggestions,
    domain: DomainDto,
    selectedKeywords?: string[] | null,
): Partial<INotificationFieldSuggestions> {
    let selections: Partial<INotificationFieldSuggestions> = {}

    if (values.title && canShowField(domain, 'title', 'content_suggestions')) {
        selections.title = variant.title
    }
    if (values.body && canShowField(domain, 'body', 'content_suggestions')) {
        selections.body = variant.body
    }
    if (values.imageUrl && canShowField(domain, 'imageUrl', 'content_suggestions')) {
        if (variant.compositeImage) {
            selections.compositeImage = variant.compositeImage
            selections.imageUrl = variant.imageUrl
        } else {
            const suggestedImage = variant.compositeImage ? extract(variant.compositeImage, 'url') : variant.imageUrl
            selections.imageUrl = suggestedImage
        }
    }
    if ((values.keywords || selectedKeywords?.length) && canShowField(domain, 'keywords', 'content_suggestions')) {
        // Always use selectedKeywords as the source of truth since it reflects
        // individual keyword selections/deselections by the user
        selections.keywords = selectedKeywords?.map((kw) => kw.toLowerCase()) ?? null
    }

    // Include tone and reason from the variant for metadata persistence
    if (variant.tone || variant.reason) {
        selections.tone = variant.tone
        selections.reason = variant.reason
    }

    return selections
}

/**
 * Transforms the new unified variantStates model to the legacy format for submission.
 * All actions use `{ type: 'replace', targetIndex }` — slot-based mapping determines
 * whether a variant overwrites an existing slot or creates a new one:
 *   - targetIndex < builderVariantCount → overwrite existing slot (replaceVariants)
 *   - targetIndex >= builderVariantCount → create new variant (addVariants)
 */
export function transformVariantStatesForSubmission(
    variantStates: Record<
        number,
        {
            enabled: boolean
            selectedAction: { type: 'replace'; targetIndex: number } | null
            selections: Partial<INotificationFieldSuggestions>
        }
    >,
    results: IEnhancedNotificationFieldSuggestions[],
    keywords: string[] | null,
    domain: DomainDto,
    builderVariantCount: number,
): {
    replaceVariants: Array<{
        variantIndex: number
        targetIndex: number
        selections: Partial<INotificationFieldSuggestions>
    }>
    addVariants: Partial<INotificationFieldSuggestions>[]
} {
    const replaceVariants: Array<{
        variantIndex: number
        targetIndex: number
        selections: Partial<INotificationFieldSuggestions>
    }> = []
    const addVariants: Partial<INotificationFieldSuggestions>[] = []

    Object.entries(variantStates).forEach(([index, state]) => {
        if (!state.enabled || !state.selectedAction) return

        const variantIndex = parseInt(index, 10)
        const variant = results[variantIndex]
        if (!variant) return

        // Build selections — respects user's checkbox choices
        const selections: Partial<INotificationFieldSuggestions> = {
            ...state.selections,
        }
        if (keywords && keywords.length > 0 && canShowField(domain, 'keywords', 'ai_recommendations')) {
            selections.keywords = keywords
        }
        if (variant.tone || variant.reason) {
            selections.tone = variant.tone
            selections.reason = variant.reason
        }

        const targetIndex = state.selectedAction.targetIndex

        if (targetIndex < builderVariantCount) {
            // Overwrite an existing builder slot
            replaceVariants.push({
                variantIndex,
                targetIndex,
                selections,
            })
        } else {
            // Create a new variant (slot beyond current builder count)
            addVariants.push(selections)
        }
    })

    return { replaceVariants, addVariants }
}
